import React, { FC, useEffect, useRef, useState } from 'react';
import { StepWizardChildProps } from 'react-step-wizard';
import { useForm, FormProvider } from 'react-hook-form';
import { GridCol, GridRow } from '@naf/grid';
import { TextVariant } from '@naf/text';
import cloneDeep from 'lodash/cloneDeep';
import { useStateMachine } from 'little-state-machine';
import { isAxiosError } from '@shared/apiClient';
import { FormFieldController } from '@components/Form/FormFieldController';
import BlockContent from '@components/block-content/BlockContent';
import { WizardNavigation } from '@pages/BecomeAMember/components/WizardNavigation';
import { DynamicForm } from '@components/block-content/types/components/Form/FormBlock.types';
import { InternalDialogArticle } from '@components/InternalDialogArticle/InternalDialogArticle';
import {
  DocumentFormData,
  getDynamicFormErrorMessage,
  hasFileValue,
  isSuccessfulDynamicFormStatus,
  validateDocumentForm,
} from '../../lib/documentForm.utils';
import { updateAction } from '../../lib/updateAction';
import { Summary } from '../Summary/Summary';
import { useTrackingStep3 } from '../../utils/useIDPTracking';
import {
  DOCUMENT_FORM_RESPONSE_ERROR,
  DOCUMENT_FORM_VALIDATION_ERROR,
  getStoredValidationError,
  removeStoredValidationError,
  UNKNOWN_DOCUMENT_FORM_ERROR,
} from './DocumentUpload.utils';
import * as S from './DocumentUpload.styles';

interface DocumentUploadProps extends Partial<StepWizardChildProps> {
  dynamicForm?: DynamicForm;
}

export const DocumentUpload: FC<DocumentUploadProps> = ({
  totalSteps,
  currentStep,
  nextStep,
  previousStep,
  dynamicForm,
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const errorMessageRef = useRef<HTMLDivElement>(null);

  const {
    actions,
    state: { idpDocumentForm, internationalDrivingPermitForm },
  } = useStateMachine({ updateAction });

  const {
    control,
    handleSubmit,
    clearErrors,
    formState,
    formState: { errors },
    getValues,
    watch,
    setValue,
    setError,
    trigger,
    reset,
    resetField,
    getFieldState,
    register,
    unregister,
    setFocus,
  } = useForm({ mode: 'onSubmit' });

  const { fieldComponents, body, title } = dynamicForm || {};

  // Initialize form values from stored state
  useEffect(() => {
    if (fieldComponents && idpDocumentForm && setValue) {
      fieldComponents.forEach((fieldComponent) => {
        if (fieldComponent.key === 'fieldComponent') {
          const storedValue = idpDocumentForm[fieldComponent.id];
          if (storedValue !== undefined) {
            // Filter out empty file arrays.
            if (Array.isArray(storedValue)) {
              if (hasFileValue(storedValue)) {
                setValue(fieldComponent.id, storedValue);
              }
            } else {
              setValue(fieldComponent.id, storedValue);
            }
          } else if (fieldComponent.initialValue !== null) {
            setValue(fieldComponent.id, fieldComponent.initialValue);
          }
        }
      });
    }
  }, [fieldComponents, idpDocumentForm, setValue]);

  useEffect(() => {
    const storedValidationError = getStoredValidationError(idpDocumentForm);

    if (storedValidationError) {
      setValidationError(storedValidationError);
      actions.updateAction({ formName: 'idpDocumentForm', data: removeStoredValidationError(idpDocumentForm) });
    }
  }, [idpDocumentForm, actions]);

  useEffect(() => {
    if (validationError && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [validationError]);

  useTrackingStep3(internationalDrivingPermitForm?.product?.productId);

  const onSubmit = async (data: DocumentFormData) => {
    const copiedData = cloneDeep(data);
    actions.updateAction({ formName: 'idpDocumentForm', data: copiedData });

    // Skip validation if membership is selected - validation will happen after consents step
    const hasSelectedMembership = !!internationalDrivingPermitForm?.membership?.productId;
    if (hasSelectedMembership) {
      if (nextStep) {
        nextStep();
      }
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      const response = await validateDocumentForm(data);

      if (isSuccessfulDynamicFormStatus(response.status)) {
        setIsValidating(false);
        if (nextStep) {
          nextStep();
        }
      } else {
        setIsValidating(false);
        setValidationError(DOCUMENT_FORM_RESPONSE_ERROR);
      }
    } catch (error) {
      setIsValidating(false);

      if (isAxiosError(error)) {
        setValidationError(getDynamicFormErrorMessage(error.response?.data) || DOCUMENT_FORM_VALIDATION_ERROR);
        return;
      }

      setValidationError(UNKNOWN_DOCUMENT_FORM_ERROR);
    }
  };

  const onGoBack = () => {};

  if (!dynamicForm || !fieldComponents) {
    return null;
  }

  return (
    <FormProvider
      control={control}
      handleSubmit={handleSubmit}
      clearErrors={clearErrors}
      setValue={setValue}
      watch={watch}
      setError={setError}
      formState={formState}
      getValues={getValues}
      trigger={trigger}
      reset={reset}
      resetField={resetField}
      getFieldState={getFieldState}
      register={register}
      unregister={unregister}
      setFocus={setFocus}
    >
      <GridRow>
        <GridCol s="12" m="12" l="8" xl="8">
          <form onSubmit={handleSubmit(onSubmit)}>
            {title && <S.Heading variant={TextVariant.Header3}>{title}</S.Heading>}
            {body && body.length > 0 && (
              <S.BodyWrapper>
                <BlockContent value={body} />
              </S.BodyWrapper>
            )}
            <S.BodyWrapper>
              <InternalDialogArticle slug="krav-til-passfoto" text="Se eksempel og les kravene her." />
            </S.BodyWrapper>
            <FormFieldController
              fieldComponents={fieldComponents}
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
              clearErrors={clearErrors}
              setError={setError}
              extraMargin
            />
            {validationError && (
              <S.ErrorMessage ref={errorMessageRef} variant={TextVariant.ArticleText}>
                {validationError}
              </S.ErrorMessage>
            )}
          </form>
        </GridCol>
        <GridCol s="12" m="12" l="4" xl="4" justify={{ s: 'stretch', m: 'stretch', l: 'end', xl: 'end' }}>
          <Summary products={internationalDrivingPermitForm?.product} />
        </GridCol>
        <GridCol s="12" m="12" l="12" xl="12">
          <WizardNavigation
            onGoback={onGoBack}
            totalSteps={totalSteps}
            currentStep={currentStep}
            nextStep={nextStep}
            previousStep={previousStep}
            isValid={Object.values(errors).length === 0 && !isValidating}
            onSubmit={onSubmit}
            getValues={getValues}
          />
        </GridCol>
      </GridRow>
    </FormProvider>
  );
};
